import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../i18n/apiError";
import { Notification } from "../types/api";
import { apiService } from "../services/api";
import { NotificationItem } from "./NotificationItem";

interface NotificationDropdownProps {
  onClose: () => void;
  onViewAll: () => void;
  onNotificationCountChange?: (count: number) => void;
  onOpenNotification?: (notification: Notification) => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onClose,
  onViewAll,
  onNotificationCountChange,
  onOpenNotification,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("notificationDropdown");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getNotifications(false, 10, 0);
      setNotifications(response.notifications || []);
      if (onNotificationCountChange) {
        onNotificationCountChange(response.unread_count || 0);
      }
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setError(translateApiError(t, err));
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    // Optimistically update UI first
    const wasUnread = notifications.find(n => n.id === id)?.read_at === null;

    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      )
    );

    // Update unread count optimistically
    if (wasUnread && onNotificationCountChange) {
      const newUnreadCount = notifications.filter(n => !n.read_at && n.id !== id).length;
      onNotificationCountChange(newUnreadCount);
    }

    try {
      await apiService.markNotificationAsRead(id);
    } catch (err: any) {
      console.error("Failed to mark notification as read:", err);
      // Rollback on error
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_at: null } : n
        )
      );
      if (wasUnread && onNotificationCountChange) {
        const rollbackCount = notifications.filter(n => !n.read_at).length;
        onNotificationCountChange(rollbackCount);
      }
    }
  };

  const handleDismiss = async (id: number) => {
    const dismissedNotification = notifications.find(n => n.id === id);
    const wasUnread = dismissedNotification?.read_at === null;

    // Optimistically remove from UI
    setNotifications((prev) => prev.filter((n) => n.id !== id));

    // Update unread count optimistically
    if (wasUnread && onNotificationCountChange) {
      const newUnreadCount = notifications.filter(n => !n.read_at && n.id !== id).length;
      onNotificationCountChange(newUnreadCount);
    }

    try {
      await apiService.dismissNotification(id);
    } catch (err: any) {
      console.error("Failed to dismiss notification:", err);
      // Rollback on error
      if (dismissedNotification) {
        setNotifications((prev) => [...prev, dismissedNotification]);
        if (wasUnread && onNotificationCountChange) {
          const rollbackCount = notifications.filter(n => !n.read_at).length;
          onNotificationCountChange(rollbackCount);
        }
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    // Save current state for rollback
    const previousNotifications = [...notifications];

    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
    );
    if (onNotificationCountChange) {
      onNotificationCountChange(0);
    }

    try {
      await apiService.markAllNotificationsAsRead();
    } catch (err: any) {
      console.error("Failed to mark all as read:", err);
      // Rollback on error
      setNotifications(previousNotifications);
      if (onNotificationCountChange) {
        const unreadCount = previousNotifications.filter(n => !n.read_at).length;
        onNotificationCountChange(unreadCount);
      }
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.metadata?.request_link) {
      navigate(notification.metadata.request_link);
      onClose();
      return;
    }
    // No external destination — open in a modal in place (handled by the
    // bell), without redirecting the user away.
    if (onOpenNotification) {
      onOpenNotification(notification);
    } else {
      onClose();
    }
  };

  return (
    <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{t("title")}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">{t("loading")}</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">{error}</div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p>{t("empty")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onRead={handleMarkAsRead}
                onDismiss={handleDismiss}
                onClick={handleNotificationClick}
                compact
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {!loading && !error && (
        <div className="border-t border-gray-200 p-3 flex items-center justify-between">
          {notifications.length > 0 ? (
            <>
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-accent hover:text-ink font-medium"
              >
                {t("markAllAsRead")}
              </button>
              <button
                onClick={onViewAll}
                className="text-sm text-accent hover:text-ink font-medium"
              >
                {t("viewAll")}
              </button>
            </>
          ) : (
            <button
              onClick={onViewAll}
              className="text-sm text-accent hover:text-ink font-medium w-full text-center"
            >
              {t("viewAllNotifications")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
